import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import CreateProjectModal from './CreateProjectModal';
import ImportExcelModal from './ImportExcelModal';
import NavigationBar from './NavigationBar';
import { useSystemSettings } from '../hooks/useSystemSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { getStageLabel } = useSystemSettings();
  const [stats, setStats] = useState(null);
  const [roleBasedData, setRoleBasedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [myProjects, setMyProjects] = useState([]);
  const [unassignedProjects, setUnassignedProjects] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/stats`);
      setStats(response.data);
      
      // Load role-based data
      await loadRoleBasedData();
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoleBasedData = async () => {
    try {
      // Load My Projects
      const myProjectsResponse = await axios.get(`${API}/dashboard/my-projects`);
      setMyProjects(myProjectsResponse.data.slice(0, 5)); // Show only first 5
      
      // Try to load Admin/Lead only data
      try {
        const unassignedResponse = await axios.get(`${API}/dashboard/unassigned-projects`);
        setUnassignedProjects(unassignedResponse.data.slice(0, 5));
        
        const teamStatsResponse = await axios.get(`${API}/dashboard/team-stats`);
        setTeamStats(teamStatsResponse.data);
      } catch (error) {
        // User is not Admin/Lead, skip these sections
        console.log('User does not have access to admin/lead data');
      }
      
      // Load recent activity
      const activityResponse = await axios.get(`${API}/dashboard/recent-activity`);
      setRecentActivity(activityResponse.data);
      
      // Calculate overdue tasks and upcoming deadlines
      const today = new Date();
      const overdue = [];
      const upcoming = [];
      
      myProjectsResponse.data.forEach(project => {
        if (project.deadline) {
          const deadline = new Date(project.deadline);
          const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
          
          if (daysUntilDeadline < 0) {
            overdue.push({ ...project, daysOverdue: Math.abs(daysUntilDeadline) });
          } else if (daysUntilDeadline <= 7) {
            upcoming.push({ ...project, daysUntilDeadline });
          }
        }
      });
      
      setOverdueTasks(overdue.slice(0, 5));
      setUpcomingDeadlines(upcoming.slice(0, 5));

      // Load weekly report
      const weeklyResponse = await axios.get(`${API}/dashboard/weekly-report`);
      setWeeklyReport(weeklyResponse.data);
      
    } catch (error) {
      console.error('Failed to load role-based data:', error);
    }
  };

  const handleProjectCreated = (newProject) => {
    // Reload stats after creating project
    loadStats();
    setShowCreateModal(false);
  };

  const handleImportComplete = () => {
    // Reload stats after import
    loadStats();
    setShowImportModal(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Use dynamic stage labels from system settings

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <NavigationBar />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="welcome-header">
              Добро пожаловать, {user?.name}!
            </h1>
            <p className="text-gray-600">
              Обзор ваших проектов и активности
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Active Projects (excluding lost) */}
            <Link 
              to="/projects-list"
              className="block bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 card-hover hover:bg-blue-50 transition-colors"
              data-testid="active-projects-card"
            >
              <div className="flex items-center">
                <div className="p-3 rounded-xl bg-blue-100">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.total_projects ? stats.total_projects - (stats.lost_projects || 0) : 0}
                  </p>
                  <p className="text-gray-600 text-sm">Активные проекты</p>
                </div>
              </div>
            </Link>

            {/* Hot Projects */}
            <Link 
              to="/projects-list"
              className="block bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 card-hover hover:bg-red-50 transition-colors"
              data-testid="hot-projects-card"
              onClick={() => {
                // Set filter for hot projects
                localStorage.setItem('projectsListFilters', JSON.stringify({
                  filters: { flag: 'hot', stages: [] },
                  showLostProjects: false,
                  sortBy: 'deadline', 
                  sortOrder: 'asc'
                }));
              }}
            >
              <div className="flex items-center">
                <div className="p-3 rounded-xl bg-red-100">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 716.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0720 13a7.975 7.975 0 01-2.343 5.657z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">{stats?.hot_projects || 0}</p>
                  <p className="text-gray-600 text-sm">Горячие проекты</p>
                </div>
              </div>
            </Link>

            {/* Pipeline Value */}
            <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 card-hover" data-testid="pipeline-value-card">
              <div className="flex items-center">
                <div className="p-3 rounded-xl bg-green-100">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {(stats?.total_pipeline_value || 0).toLocaleString()} ₸
                  </p>
                  <p className="text-gray-600 text-sm">Общая стоимость</p>
                </div>
              </div>
            </div>

            {/* Lost Projects */}
            <Link 
              to="/projects-list"
              className="block bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 card-hover hover:bg-gray-50 transition-colors"
              data-testid="lost-projects-card"
              onClick={() => {
                // Show lost projects and filter to only lost stage
                localStorage.setItem('projectsListFilters', JSON.stringify({
                  filters: { stages: ['lost'] },
                  showLostProjects: true,
                  sortBy: 'deadline', 
                  sortOrder: 'asc'
                }));
              }}
            >
              <div className="flex items-center">
                <div className="p-3 rounded-xl bg-gray-100">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">{stats?.lost_projects || 0}</p>
                  <p className="text-gray-600 text-sm">Проигранные</p>
                  <p className="text-xs text-gray-500">скрыты по умолчанию</p>
                </div>
              </div>
            </Link>
          </div>

          {/* Projects by Stage */}
          <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Проекты по этапам</h3>
            <div className="space-y-3">
              {stats?.stages_breakdown && Object.entries(stats.stages_breakdown).map(([stage, count]) => (
                <div key={stage} className="flex justify-between items-center">
                  <span className="text-gray-600">{getStageLabel(stage)}</span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Role-based sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* My Projects */}
            <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Мои проекты</h3>
                <Link to="/projects-list" className="text-blue-600 hover:text-blue-700 text-sm">
                  Все проекты →
                </Link>
              </div>
              <div className="space-y-3">
                {myProjects.length > 0 ? (
                  myProjects.map((project) => (
                    <Link 
                      key={project.id} 
                      to={`/project/${project.id}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        // Save referrer info for smart back navigation
                        localStorage.setItem('projectDetailReferrer', JSON.stringify({
                          path: '/dashboard'
                        }));
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900 hover:text-blue-600">{project.name}</h4>
                          <p className="text-sm text-gray-600">{getStageLabel(project.stage)}</p>
                        </div>
                        <span className="text-sm font-medium text-green-600">
                          {project.budget_amount?.toLocaleString()} ₸
                        </span>
                      </div>
                      {project.deadline && (
                        <p className="text-xs text-gray-500 mt-1">
                          Срок: {new Date(project.deadline).toLocaleDateString('ru-RU')}
                        </p>
                      )}
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">Нет назначенных проектов</p>
                )}
              </div>
            </div>

            {/* Weekly Report */}
            <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Report</h3>
              {weeklyReport ? (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Projects created this week:</span>
                    <span className="font-semibold">{weeklyReport.projects_created_this_week}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Deals closed this week:</span>
                    <span className="font-semibold">{weeklyReport.deals_closed_this_week}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Week over week growth:</span>
                    <span className={`font-semibold ${
                      weeklyReport.week_over_week_growth >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(weeklyReport.week_over_week_growth || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Нет данных для отчета</p>
              )}
            </div>
          </div>

          {/* Activity & Deadlines */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
            {/* Overdue Tasks */}
            {overdueTasks.length > 0 && (
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-red-700 mb-4">Просроченные задачи</h3>
                <div className="space-y-3">
                  {overdueTasks.map((project) => (
                    <Link 
                      key={project.id} 
                      to={`/project/${project.id}`}
                      className="block p-3 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-colors"
                      onClick={() => {
                        localStorage.setItem('projectDetailReferrer', JSON.stringify({
                          path: '/dashboard'
                        }));
                      }}
                    >
                      <h4 className="font-medium text-gray-900 hover:text-red-700">{project.name}</h4>
                      <p className="text-sm text-red-600">Просрочено на {project.daysOverdue} дн.</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Deadlines */}
            {upcomingDeadlines.length > 0 && (
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-orange-700 mb-4">Приближающиеся дедлайны</h3>
                <div className="space-y-3">
                  {upcomingDeadlines.map((project) => (
                    <Link 
                      key={project.id} 
                      to={`/project/${project.id}`}
                      className="block p-3 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors"
                      onClick={() => {
                        localStorage.setItem('projectDetailReferrer', JSON.stringify({
                          path: '/dashboard'
                        }));
                      }}
                    >
                      <h4 className="font-medium text-gray-900 hover:text-orange-700">{project.name}</h4>
                      <p className="text-sm text-orange-600">Осталось {project.daysUntilDeadline} дн.</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Admin/Lead only sections */}
          {(unassignedProjects.length > 0 || teamStats) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
              {/* Unassigned Projects (Admin/Lead only) */}
              {unassignedProjects.length > 0 && (
                <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Неназначенные проекты</h3>
                  <div className="space-y-3">
                    {unassignedProjects.map((project) => (
                      <Link 
                        key={project.id} 
                        to={`/project/${project.id}`}
                        className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          localStorage.setItem('projectDetailReferrer', JSON.stringify({
                            path: '/dashboard'
                          }));
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900 hover:text-blue-600">{project.name}</h4>
                            <p className="text-sm text-gray-600">{getStageLabel(project.stage)}</p>
                          </div>
                          <span className="text-sm text-orange-600 font-medium">Не назначен</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Team Stats (Admin/Lead only) */}
              {teamStats && (
                <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Статистика команды</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Всего менеджеров:</span>
                      <span className="font-semibold">{teamStats.total_managers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Активных проектов:</span>
                      <span className="font-semibold">{teamStats.active_projects}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Средняя загрузка:</span>
                      <span className="font-semibold">{(teamStats.average_load || 0).toFixed(1)} проектов/менеджер</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Management Actions (Admin only) */}
          {user?.role === 'admin' && (
            <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Управление</h3>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/admin/users"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Управление пользователями
                </Link>
                <Link
                  to="/admin/settings"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Настройки системы
                </Link>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-4 mt-8">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              data-testid="create-project-button"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Создать проект
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary"
              data-testid="import-excel-button"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Импорт Excel
            </button>
          </div>
        </div>
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <ImportExcelModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
};

export default Dashboard;